import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { wsManuscriptLatexErrorFixUrl } from "utils/urls";

function createRequestId() {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    return `latex-error-fix-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function useLatexErrorFixWebSocket({ spaceUuid, manuscriptId, nodeId, enabled = true }) {
    const wsRef = useRef(null);
    const pendingMessageRef = useRef(null);
    const currentRequestIdRef = useRef(null);
    const [status, setStatus] = useState("idle");
    const [summary, setSummary] = useState("");
    const [replacements, setReplacements] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [isConnected, setIsConnected] = useState(false);

    const connectionReady = useMemo(
        () => enabled && !!spaceUuid && !!manuscriptId && !!nodeId,
        [enabled, manuscriptId, nodeId, spaceUuid],
    );

    const handleSocketMessage = useCallback((event) => {
        const data = JSON.parse(event.data || "{}");
        const type = data?.type;
        const payload = data?.payload || {};
        const requestId = payload?.request_id;

        if (requestId && requestId !== currentRequestIdRef.current) {
            return;
        }

        if (type === "latex_error_fix.started") {
            setStatus("running");
            setSummary("");
            setReplacements([]);
            setErrorMessage("");
            return;
        }

        if (type === "latex_error_fix.completed") {
            setStatus("completed");
            setSummary(payload.summary || "");
            setReplacements(Array.isArray(payload.replacements) ? payload.replacements : []);
            return;
        }

        if (type === "latex_error_fix.stopped") {
            setStatus("stopped");
            setSummary(payload.summary || "");
            setReplacements(Array.isArray(payload.replacements) ? payload.replacements : []);
            return;
        }

        if (type === "latex_error_fix.error") {
            setStatus("error");
            setErrorMessage(payload.message || "LaTeX 오류 수정 요청에 실패했습니다.");
        }
    }, []);

    const connectWebSocket = useCallback(() => {
        if (!connectionReady || wsRef.current) {
            return;
        }

        const socket = new WebSocket(wsManuscriptLatexErrorFixUrl(spaceUuid, manuscriptId, nodeId));
        wsRef.current = socket;

        socket.onopen = () => {
            setIsConnected(true);
            if (pendingMessageRef.current) {
                socket.send(JSON.stringify(pendingMessageRef.current));
                pendingMessageRef.current = null;
            }
        };

        socket.onmessage = handleSocketMessage;
        socket.onerror = () => {
            setErrorMessage("LaTeX 오류 수정 소켓 연결에 실패했습니다.");
        };
        socket.onclose = () => {
            setIsConnected(false);
            wsRef.current = null;
        };
    }, [connectionReady, handleSocketMessage, manuscriptId, nodeId, spaceUuid]);

    useEffect(() => {
        if (connectionReady) {
            connectWebSocket();
        }

        return () => {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [connectWebSocket, connectionReady]);

    const sendOrQueue = useCallback(
        (message) => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify(message));
                return;
            }

            pendingMessageRef.current = message;
            connectWebSocket();
        },
        [connectWebSocket],
    );

    const startRequest = useCallback(
        ({ currentContent, diagnostics }) => {
            const requestId = createRequestId();
            currentRequestIdRef.current = requestId;
            setStatus("connecting");
            setSummary("");
            setReplacements([]);
            setErrorMessage("");

            sendOrQueue({
                action: "start",
                payload: {
                    request_id: requestId,
                    current_content: currentContent,
                    diagnostics,
                },
            });

            return requestId;
        },
        [sendOrQueue],
    );

    const stopRequest = useCallback(() => {
        if (!currentRequestIdRef.current) {
            return;
        }

        sendOrQueue({
            action: "stop",
            payload: {
                request_id: currentRequestIdRef.current,
            },
        });
    }, [sendOrQueue]);

    const reset = useCallback(() => {
        currentRequestIdRef.current = null;
        setStatus("idle");
        setSummary("");
        setReplacements([]);
        setErrorMessage("");
    }, []);

    return {
        isConnected,
        isRunning: status === "running" || status === "connecting",
        status,
        summary,
        replacements,
        errorMessage,
        hasResult: replacements.length > 0,
        startRequest,
        stopRequest,
        reset,
    };
}

export default useLatexErrorFixWebSocket;
